import { create } from 'zustand';
import { UpgradeRequest } from '../types/upgrade';
import { collection, doc, setDoc, query, where, onSnapshot, updateDoc, writeBatch, getDocs, deleteDoc, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { useBansStore } from './bansStore';
import { useSuspensionStore } from './suspensionStore';
import toast from 'react-hot-toast';

type UpgradeState = {
  requests: Record<string, UpgradeRequest>;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  initializeListener: () => () => void;
  createRequest: (request: Omit<UpgradeRequest, 'id' | 'status' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  approveRequest: (requestId: string, price: number) => Promise<void>;
  rejectRequest: (requestId: string, reason: string) => Promise<void>;
  submitPayment: (requestId: string, paymentProofUrl?: string) => Promise<void>;
  completeUpgrade: (requestId: string) => Promise<void>;
  cancelRequest: (requestId: string) => Promise<void>;
  acceptPayment: (requestId: string) => Promise<void>;
  rejectPayment: (requestId: string) => Promise<void>;
  rejectPaidUpgrade: (requestId: string, reason: string) => Promise<void>;
  bulkConfirmReject: (confirmations: Array<{
    passportNumber: string;
    hotel: string;
    status: 'confirm' | 'reject';
    reason?: string;
  }>) => Promise<{
    processed: number;
    succeeded: number;
    failed: number;
    errors: any[];
  }>;
};

export const useUpgradeStore = create<UpgradeState>((set, get) => {
  let unsubscribe: (() => void) | null = null;

  return {
    requests: {},
    loading: false,
    error: null,
    initialized: false,

    initializeListener: () => {
      if (get().initialized) return () => {};

      const userRole = localStorage.getItem('userRole');
      const userId = localStorage.getItem('userId');

      let q;
      if (userRole === 'guide') {
        q = query(
          collection(firestore, 'UpgradeRequests'),
          where('guideId', '==', userId)
        );
      } else if (userRole === 'organizer') {
        q = query(
          collection(firestore, 'UpgradeRequests'),
          where('organizerId', '==', userId)
        );
      } else {
        q = collection(firestore, 'UpgradeRequests');
      }

      unsubscribe = onSnapshot(q, (snapshot) => {
        const requests: Record<string, UpgradeRequest> = {};
        snapshot.forEach((doc) => {
          requests[doc.id] = {
            ...doc.data(),
            id: doc.id
          } as UpgradeRequest;
        });
        set({ requests, initialized: true });
      }, (error) => {
        console.error('Error in upgrade requests listener:', error);
        set({ error: error.message, initialized: false });
      });

      return () => {
        if (unsubscribe) {
          unsubscribe();
          unsubscribe = null;
        }
      };
    },

    createRequest: async (requestData) => {
      set({ loading: true, error: null });
      try {
        // REMOVED the all_accommodation suspension check since room upgrades 
        // should not be affected by accommodation suspension
        
        // Only check for user-specific ban
        const isUserBanned = await useBansStore
          .getState()
          .checkUserBans(requestData.guideId, 'accommodation');

        if (isUserBanned) {
          throw new Error('You are banned from using the accommodation service');
        }
        
        // Check for existing requests for the same pilgrim and hotel
        console.log(`Checking for existing upgrade requests for pilgrim: ${requestData.pilgrimId} in hotel: ${requestData.fromHotel}`);
        const upgradeRequestsRef = collection(firestore, 'UpgradeRequests');
        const existingRequestQuery = query(
          upgradeRequestsRef,
          where('pilgrimId', '==', requestData.pilgrimId),
          where('fromHotel', '==', requestData.fromHotel)
        );
        
        const existingRequestsSnapshot = await getDocs(existingRequestQuery);
        
        if (!existingRequestsSnapshot.empty) {
          const existingRequest = existingRequestsSnapshot.docs[0].data();
          console.log(`Found existing request with status: ${existingRequest.status}`);
          throw new Error(`An upgrade request already exists for this pilgrim in ${requestData.fromHotel}. Please check the existing request.`);
        }

        const requestRef = doc(collection(firestore, 'UpgradeRequests'));
        
        // Ensure all required fields have valid values to prevent Firestore errors
        const request: UpgradeRequest = {
          ...requestData,
          pilgrimId: requestData.pilgrimId || '',
          pilgrimName: requestData.pilgrimName || '',
          passportNumber: requestData.passportNumber || '',
          guideId: requestData.guideId || '',
          guideName: requestData.guideName || 'Unknown Guide', // Provide default value
          organizerId: requestData.organizerId || '',
          organizerName: requestData.organizerName || 'Unknown Organizer', // Provide default value
          fromHotel: requestData.fromHotel || '',
          toHotel: requestData.toHotel || '',
          fromRoomType: requestData.fromRoomType || 'triple',
          toRoomType: requestData.toRoomType || 'double',
          startDate: requestData.startDate || '',
          endDate: requestData.endDate || '',
          id: requestRef.id,
          status: 'pending',
          createdAt: Date.now(),
          updatedAt: Date.now()
        };

        await setDoc(requestRef, request);

        set(state => ({
          requests: {
            ...state.requests,
            [request.id]: request
          },
          loading: false
        }));
      } catch (error: any) {
        console.error('Error creating upgrade request:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    approveRequest: async (requestId: string, price: number) => {
      set({ loading: true, error: null });
      try {
        const userId = localStorage.getItem('userId');
        if (!userId) throw new Error('User not authenticated');

        await updateDoc(doc(firestore, 'UpgradeRequests', requestId), {
          status: 'payment_pending',
          price,
          approvedBy: userId,
          approvedAt: Date.now(),
          updatedAt: Date.now()
        });

        set(state => ({
          requests: {
            ...state.requests,
            [requestId]: {
              ...state.requests[requestId],
              status: 'payment_pending',
              price,
              approvedBy: userId,
              approvedAt: Date.now(),
              updatedAt: Date.now()
            }
          },
          loading: false
        }));
      } catch (error: any) {
        console.error('Error approving request:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    rejectRequest: async (requestId: string, reason: string) => {
      set({ loading: true, error: null });
      try {
        await updateDoc(doc(firestore, 'UpgradeRequests', requestId), {
          status: 'rejected',
          rejectionReason: reason,
          updatedAt: Date.now()
        });

        set(state => ({
          requests: {
            ...state.requests,
            [requestId]: {
              ...state.requests[requestId],
              status: 'rejected',
              rejectionReason: reason,
              updatedAt: Date.now()
            }
          },
          loading: false
        }));
      } catch (error: any) {
        console.error('Error rejecting request:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    submitPayment: async (requestId: string, paymentProofUrl?: string) => {
      set({ loading: true, error: null });
      try {
        await updateDoc(doc(firestore, 'UpgradeRequests', requestId), {
          status: 'paid',
          // If payment proof is provided (for backward compatibility), include it
          ...(paymentProofUrl ? {
            paymentProof: {
              url: paymentProofUrl,
              uploadedAt: Date.now()
            }
          } : {}),
          updatedAt: Date.now()
        });

        set(state => ({
          requests: {
            ...state.requests,
            [requestId]: {
              ...state.requests[requestId],
              status: 'paid',
              ...(paymentProofUrl ? {
                paymentProof: {
                  url: paymentProofUrl,
                  uploadedAt: Date.now()
                }
              } : {}),
              updatedAt: Date.now()
            }
          },
          loading: false
        }));
      } catch (error: any) {
        console.error('Error confirming payment:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    completeUpgrade: async (requestId: string) => {
      set({ loading: true, error: null });
      try {
        console.log('Starting upgrade completion process for request:', requestId);
        const batch = writeBatch(firestore);
        const request = get().requests[requestId];

        if (!request) throw new Error('Request not found');

        // Update request status
        const requestRef = doc(firestore, 'UpgradeRequests', requestId);
        batch.update(requestRef, {
          status: 'completed',
          completedAt: Date.now(),
          updatedAt: Date.now()
        });

        // 1. Remove current accommodation
        console.log('Checking for existing accommodations to remove');
        const hotelTablesRef = collection(firestore, 'AccommodationTables');
        const tablesSnapshot = await getDocs(hotelTablesRef);
        
        // Keep track of which tables and rooms were updated
        let accommodationFound = false;
        let accommodationUpdated = false;
        
        for (const tableDoc of tablesSnapshot.docs) {
          const tableData = tableDoc.data();
          
          // Only process tables in the target hotel
          if (tableData.hn !== request.fromHotel) continue;
          
          console.log(`Checking accommodations in table: ${tableData.tn}`);
          let tableUpdated = false;
          
          // Process each room in the table
          const updatedRooms = tableData.r.map((room: any) => {
            let roomUpdated = false;
            
            // Process each bed in the room
            const updatedBeds = room.b.map((bed: any) => {
              // Check if pilgrim is assigned to this bed
              if (bed.a) {
                const assignmentIndex = bed.a.findIndex((a: any) => a.pid === request.pilgrimId);
                
                if (assignmentIndex >= 0) {
                  accommodationFound = true;
                  
                  // Remove the assignment from the bed
                  const updatedAssignments = [...bed.a];
                  updatedAssignments.splice(assignmentIndex, 1);
                  
                  roomUpdated = true;
                  accommodationUpdated = true;
                  
                  console.log(`Found pilgrim in room ${room.n}, removing assignment`);
                  
                  return {
                    ...bed,
                    a: updatedAssignments
                  };
                }
              }
              
              return bed;
            });
            
            // Update room occupancy if needed
            if (roomUpdated) {
              const newOccupancy = updatedBeds.reduce((count: number, bed: any) => {
                return count + (bed.a ? bed.a.length : 0);
              }, 0);
              
              tableUpdated = true;
              
              return {
                ...room,
                b: updatedBeds,
                o: newOccupancy
              };
            }
            
            return room;
          });
          
          // Update the table if changes were made
          if (tableUpdated) {
            console.log(`Updating table: ${tableData.tn}`);
            batch.update(tableDoc.ref, {
              r: updatedRooms,
              ua: Date.now()
            });
          }
        }
        
        if (!accommodationFound) {
          console.log('No existing accommodation found to remove');
        } else if (!accommodationUpdated) {
          console.log('Accommodation found but not updated');
        }

        // 2. Update pilgrim selector room type
        console.log('Updating PilgrimSelector room type');
        const selectorRef = collection(firestore, 'PilgrimSelector');
        const selectorQuery = query(selectorRef, where('pilgrimId', '==', request.pilgrimId));
        const selectorSnapshot = await getDocs(selectorQuery);

        if (!selectorSnapshot.empty) {
          const selectorDoc = selectorSnapshot.docs[0];
          const selectorData = selectorDoc.data();
          
          // Update accommodation status for this hotel
          const updatedAccommodation = { ...selectorData.isAccommodated } || {};
          updatedAccommodation[request.fromHotel] = false;
          
          // Update room type based on hotel
          let roomTypeUpdates = {};
          
          if (request.fromHotel === selectorData.packageData.mainMakkah.hotel) {
            roomTypeUpdates = {
              'packageData.mainMakkah.roomType': request.toRoomType,
              'isAccommodated': updatedAccommodation,
              'updatedAt': Date.now()
            };
          } else if (
            selectorData.packageData.shiftingMakkah && 
            request.fromHotel === selectorData.packageData.shiftingMakkah.hotel
          ) {
            roomTypeUpdates = {
              'packageData.shiftingMakkah.roomType': request.toRoomType,
              'isAccommodated': updatedAccommodation,
              'updatedAt': Date.now()
            };
          } else if (request.fromHotel === selectorData.packageData.madinah.hotel) {
            roomTypeUpdates = {
              'packageData.madinah.roomType': request.toRoomType,
              'isAccommodated': updatedAccommodation,
              'updatedAt': Date.now()
            };
          }
          
          batch.update(selectorDoc.ref, roomTypeUpdates);
        }

        // 3. Update room type in pilgrim collection
        console.log('Updating Pilgrim room type');
        const pilgrimRef = doc(firestore, 'Pilgrims', request.pilgrimId);
        const pilgrimDoc = await getDoc(pilgrimRef);
        
        if (pilgrimDoc.exists()) {
          const packageData = pilgrimDoc.data().package_nickName;
          
          // Get room type field based on hotel
          let roomTypeField = '';
          
          // Determine which room type to update based on hotel
          const packageRef = collection(firestore, 'Packages');
          const packageQuery = query(packageRef, where('name', '==', packageData));
          const packageSnapshot = await getDocs(packageQuery);
          
          if (!packageSnapshot.empty) {
            const packageDoc = packageSnapshot.docs[0];
            const packageData = packageDoc.data();
            
            if (request.fromHotel === packageData.mainMakkahHotel) {
              roomTypeField = 'main_makkah_roomType';
            } else if (request.fromHotel === packageData.shiftingMakkahHotel) {
              roomTypeField = 'shifting_makkah_roomType';
            } else if (request.fromHotel === packageData.madinahHotel) {
              roomTypeField = 'madinah_roomType';
            }
          }
          
          if (roomTypeField) {
            // Update the specific room type field
            const pilgrimUpdate = {
              [roomTypeField]: request.toRoomType,
              updatedAt: Date.now()
            };
            
            console.log(`Updating pilgrim ${request.pilgrimName} with new room type:`, pilgrimUpdate);
            batch.update(pilgrimRef, pilgrimUpdate);
          }
        }

        // Commit all changes
        console.log('Committing all changes');
        await batch.commit();

        // Update local state
        set(state => ({
          requests: {
            ...state.requests,
            [requestId]: {
              ...state.requests[requestId],
              status: 'completed',
              completedAt: Date.now(),
              updatedAt: Date.now()
            }
          },
          loading: false
        }));
        
        console.log('Upgrade completion process finished successfully');
      } catch (error: any) {
        console.error('Error completing upgrade:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    cancelRequest: async (requestId: string) => {
      set({ loading: true, error: null });
      try {
        const request = get().requests[requestId];
        if (!request) throw new Error('Request not found');
        
        // If the request is completed, we need to revert the room type changes
        if (request.status === 'completed') {
          console.log('Cancelling a completed upgrade - will revert room type changes');
          const batch = writeBatch(firestore);
          
          // 1. Update PilgrimSelector room type back to original
          console.log('Reverting PilgrimSelector room type');
          const selectorRef = collection(firestore, 'PilgrimSelector');
          const selectorQuery = query(selectorRef, where('pilgrimId', '==', request.pilgrimId));
          const selectorSnapshot = await getDocs(selectorQuery);

          if (!selectorSnapshot.empty) {
            const selectorDoc = selectorSnapshot.docs[0];
            const selectorData = selectorDoc.data();
            
            // Update room type based on hotel
            let roomTypeUpdates = {};
            
            if (request.fromHotel === selectorData.packageData.mainMakkah.hotel) {
              roomTypeUpdates = {
                'packageData.mainMakkah.roomType': request.fromRoomType,
                'updatedAt': Date.now()
              };
            } else if (
              selectorData.packageData.shiftingMakkah && 
              request.fromHotel === selectorData.packageData.shiftingMakkah.hotel
            ) {
              roomTypeUpdates = {
                'packageData.shiftingMakkah.roomType': request.fromRoomType,
                'updatedAt': Date.now()
              };
            } else if (request.fromHotel === selectorData.packageData.madinah.hotel) {
              roomTypeUpdates = {
                'packageData.madinah.roomType': request.fromRoomType,
                'updatedAt': Date.now()
              };
            }
            
            batch.update(selectorDoc.ref, roomTypeUpdates);
          }

          // 2. Update room type in pilgrim collection
          console.log('Reverting Pilgrim room type');
          const pilgrimRef = doc(firestore, 'Pilgrims', request.pilgrimId);
          const pilgrimDoc = await getDoc(pilgrimRef);
          
          if (pilgrimDoc.exists()) {
            const packageData = pilgrimDoc.data().package_nickName;
            
            // Get room type field based on hotel
            let roomTypeField = '';
            
            // Determine which room type to update based on hotel
            const packageRef = collection(firestore, 'Packages');
            const packageQuery = query(packageRef, where('name', '==', packageData));
            const packageSnapshot = await getDocs(packageQuery);
            
            if (!packageSnapshot.empty) {
              const packageDoc = packageSnapshot.docs[0];
              const packageData = packageDoc.data();
              
              if (request.fromHotel === packageData.mainMakkahHotel) {
                roomTypeField = 'main_makkah_roomType';
              } else if (request.fromHotel === packageData.shiftingMakkahHotel) {
                roomTypeField = 'shifting_makkah_roomType';
              } else if (request.fromHotel === packageData.madinahHotel) {
                roomTypeField = 'madinah_roomType';
              }
            }
            
            if (roomTypeField) {
              // Update the specific room type field back to original
              const pilgrimUpdate = {
                [roomTypeField]: request.fromRoomType,
                updatedAt: Date.now()
              };
              
              console.log(`Reverting pilgrim ${request.pilgrimName} room type back to:`, pilgrimUpdate);
              batch.update(pilgrimRef, pilgrimUpdate);
            }
          }
          
          // Delete the request document
          batch.delete(doc(firestore, 'UpgradeRequests', requestId));
          
          // Commit all changes
          await batch.commit();
        } else {
          // For non-completed requests, just delete the request
          await deleteDoc(doc(firestore, 'UpgradeRequests', requestId));
        }

        // Update local state by removing the request
        set(state => {
          const newRequests = { ...state.requests };
          delete newRequests[requestId];
          return { 
            requests: newRequests,
            loading: false 
          };
        });
      } catch (error: any) {
        console.error('Error cancelling request:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    acceptPayment: async (requestId: string) => {
      set({ loading: true, error: null });
      try {
        // Directly transition to 'paid' status
        await updateDoc(doc(firestore, 'UpgradeRequests', requestId), {
          status: 'paid',
          updatedAt: Date.now()
        });

        set(state => ({
          requests: {
            ...state.requests,
            [requestId]: {
              ...state.requests[requestId],
              status: 'paid',
              updatedAt: Date.now()
            }
          },
          loading: false
        }));
      } catch (error: any) {
        console.error('Error accepting payment:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    rejectPayment: async (requestId: string) => {
      set({ loading: true, error: null });
      try {
        // Delete the request when payment is rejected
        await deleteDoc(doc(firestore, 'UpgradeRequests', requestId));

        // Update local state by removing the request
        set(state => {
          const newRequests = { ...state.requests };
          delete newRequests[requestId];
          return { 
            requests: newRequests,
            loading: false 
          };
        });
      } catch (error: any) {
        console.error('Error rejecting payment:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    rejectPaidUpgrade: async (requestId: string, reason: string) => {
      set({ loading: true, error: null });
      try {
        // Update the request to rejected status
        await updateDoc(doc(firestore, 'UpgradeRequests', requestId), {
          status: 'rejected',
          rejectionReason: reason,
          updatedAt: Date.now()
        });

        // Update local state
        set(state => ({
          requests: {
            ...state.requests,
            [requestId]: {
              ...state.requests[requestId],
              status: 'rejected',
              rejectionReason: reason,
              updatedAt: Date.now()
            }
          },
          loading: false
        }));
      } catch (error: any) {
        console.error('Error rejecting paid upgrade:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    bulkConfirmReject: async (confirmations) => {
      set({ loading: true, error: null });
      try {
        console.log(`Processing ${confirmations.length} confirmations/rejections`);
        
        const results = {
          processed: confirmations.length,
          succeeded: 0,
          failed: 0,
          errors: [] as any[]
        };
        
        // Process each confirmation
        for (const confirmation of confirmations) {
          try {
            // Find the request by passport number and hotel
            const requestsRef = collection(firestore, 'UpgradeRequests');
            const requestQuery = query(
              requestsRef,
              where('passportNumber', '==', confirmation.passportNumber),
              where('fromHotel', '==', confirmation.hotel),
              where('status', '==', 'paid')
            );
            
            const requestSnapshot = await getDocs(requestQuery);
            
            if (requestSnapshot.empty) {
              results.failed++;
              results.errors.push({
                passport: confirmation.passportNumber,
                hotel: confirmation.hotel,
                error: 'No paid upgrade request found'
              });
              continue;
            }
            
            const requestDoc = requestSnapshot.docs[0];
            const requestId = requestDoc.id;
            
            if (confirmation.status === 'confirm') {
              // Complete the upgrade
              await get().completeUpgrade(requestId);
              results.succeeded++;
            } else {
              // Reject the upgrade
              await get().rejectPaidUpgrade(
                requestId, 
                confirmation.reason || 'Rejected in bulk processing'
              );
              results.succeeded++;
            }
          } catch (error: any) {
            console.error('Error processing confirmation:', error);
            results.failed++;
            results.errors.push({
              passport: confirmation.passportNumber,
              hotel: confirmation.hotel,
              error: error.message || 'Unknown error'
            });
          }
        }
        
        set({ loading: false });
        return results;
      } catch (error: any) {
        console.error('Error in bulk confirm/reject:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    }
  };
});